import {
  type AdminMessageInfo,
  COMPLETION_CHECKER_AGENT_NAME,
  getAdvisorQuestion,
  getModelDisplayName,
  getReasoningLevelDisplayLabel,
  type SandboxTarget,
} from '@kyumo/common';
import { CompletionCheckPendingView } from '@kyumo/shared-ui';
import { Bot, Clock, Cog, User } from 'lucide-react';
import { formatDateTime, formatDuration, formatFullDateTime } from '../utilities';
import { AdvisorResultCard } from './AdvisorResultCard';
import { ApprovalResultCard } from './ApprovalResultCard';
import { CompletionCheckCard } from './CompletionCheckCard';
import { CompressionCard } from './CompressionCard';
import { formatCost, formatNumber } from './chart-utils';
import { FileAttachmentCard, FileSubmissionCard } from './FileCards';
import { HookExecutionCard } from './HookExecutionCard';
import { MarkdownContent, ReasoningCard } from './MarkdownContent';
import { ShellCommandCard } from './ShellCommandCard';
import { ExternalAgentMessagesCard, SubAgentMessagesCard } from './SubAgentCards';
import type { ExtractedPrompt } from './SystemPromptSection';
import { ToolCallCard } from './ToolCallCard';
import { TurnEndCard } from './TurnEndCard';
import type {
  ApprovalResultPart,
  BuiltinCommandPart,
  CompressionPart,
  ExternalAgentMessageInfo,
  HookEventPart,
  MessagePart,
  ReasoningPart,
  ShellCommandPart,
  SubAgentCompressionInfo,
  SubAgentMessageInfo,
  TextPart,
  ToolCallPart,
  ToolResultPart,
  TurnEndPart,
} from './task-detail-types';
import {
  getToolStatus,
  groupSubAgentMessagesBySubTaskId,
  groupToolParts,
  isFileAttachmentPart,
  parseFileSubmissionResult,
} from './task-detail-utils';
import { WidgetPreview } from './WidgetPreview';

/**
 * メッセージ内容のレンダリング（MessageCardとGroupedAssistantCardで共通使用）
 */
function MessageContent({
  message,
  subagentPrompts,
  sandboxTarget,
}: {
  message: AdminMessageInfo;
  subagentPrompts?: ExtractedPrompt[] | undefined;
  /** このタスクがどの実行先で実行されたか（サンドボックスのツールに表示する） */
  sandboxTarget?: SandboxTarget | undefined;
}) {
  const parts = (message.parts ?? []) as MessagePart[];
  const { nonToolParts, toolGroups } = groupToolParts(parts);
  const hasContent = message.content || parts.length > 0;

  if (!hasContent) {
    return <p className="text-muted-foreground italic">（内容なし）</p>;
  }

  return (
    <div className="space-y-3">
      {parts.length > 0 ? (
        <>
          {/* 全パートを元の順序でレンダリング */}
          {(() => {
            type RenderItem =
              | { type: 'nonTool'; index: number; part: MessagePart }
              | {
                  type: 'toolGroup';
                  index: number;
                  toolCallId: string;
                  call: ToolCallPart | undefined;
                  result: ToolResultPart | undefined;
                };

            const renderItems: RenderItem[] = [];

            nonToolParts.forEach(({ index, part }) => {
              renderItems.push({ type: 'nonTool', index, part });
            });

            toolGroups.forEach((group, toolCallId) => {
              renderItems.push({
                type: 'toolGroup',
                index: group.index,
                toolCallId,
                call: group.call,
                result: group.result,
              });
            });

            renderItems.sort((a, b) => a.index - b.index);

            return renderItems.map((item) => {
              const key = `${message.id}-part-${item.index}`;

              if (item.type === 'nonTool') {
                const part = item.part;

                if (part.type === 'text' && 'text' in part) {
                  if (message.role === 'user') {
                    return (
                      <p key={key} className="whitespace-pre-wrap text-foreground">
                        {(part as TextPart).text}
                      </p>
                    );
                  }
                  return (
                    <div key={key} className="prose prose-invert prose-sm max-w-none">
                      <MarkdownContent content={(part as TextPart).text} />
                    </div>
                  );
                }

                if (part.type === 'reasoning' && 'text' in part) {
                  return <ReasoningCard key={key} content={(part as ReasoningPart).text} />;
                }

                if (isFileAttachmentPart(part)) {
                  return <FileAttachmentCard key={key} attachment={part} />;
                }

                if (part.type === 'compression') {
                  return <CompressionCard key={key} part={part as CompressionPart} />;
                }

                if (part.type === 'builtin-command') {
                  // 入力したコマンドをそのまま見せる（エージェントへの発言ではないため等幅で区別する）
                  const commandPart = part as BuiltinCommandPart;
                  return (
                    <p key={key} className="font-mono text-sm text-foreground">
                      /{commandPart.command}
                      {commandPart.args ? ` ${commandPart.args}` : ''}
                    </p>
                  );
                }

                if (part.type === 'shell-command') {
                  // 利用者が `!` で実行したコマンド。AI のツール実行と混同しないよう
                  // 専用のカードで出す
                  return <ShellCommandCard key={key} part={part as ShellCommandPart} />;
                }

                if (part.type === 'approval-result') {
                  return <ApprovalResultCard key={key} part={part as ApprovalResultPart} />;
                }

                if (part.type === 'hook-event') {
                  return <HookExecutionCard key={key} part={part as HookEventPart} />;
                }

                // ターンの終わり方（監査用の終了記録）。ターンの区切りとして表示する
                if (part.type === 'turn-end') {
                  return <TurnEndCard key={key} part={part as TurnEndPart} />;
                }

                return null;
              }

              if (item.type === 'toolGroup') {
                const { call, result } = item;
                const toolName = call?.toolName || result?.toolName || 'unknown';

                let status: 'running' | 'completed' | 'failed' | 'cancelled';
                if (result) {
                  status = result.isError
                    ? 'failed'
                    : result.isCancelled
                      ? 'cancelled'
                      : 'completed';
                } else if (call) {
                  status = getToolStatus(call.status);
                } else {
                  status = 'running';
                }

                const rawSubAgentMessages =
                  call?.streamingSubAgentMessages || result?.subAgentMessages;
                const subAgentMessages = rawSubAgentMessages?.filter(
                  (entry): entry is SubAgentMessageInfo | SubAgentCompressionInfo =>
                    !('type' in entry && entry.type === 'step-usage'),
                );

                const agentName = (() => {
                  if (!result?.result || typeof result.result !== 'object') return undefined;
                  const resultObj = result.result as Record<string, unknown>;
                  if ('agentName' in resultObj && typeof resultObj.agentName === 'string') {
                    return resultObj.agentName;
                  }
                  if (
                    'generatedAgent' in resultObj &&
                    typeof resultObj.generatedAgent === 'object' &&
                    resultObj.generatedAgent !== null
                  ) {
                    const generatedAgent = resultObj.generatedAgent as Record<string, unknown>;
                    if ('name' in generatedAgent && typeof generatedAgent.name === 'string') {
                      return generatedAgent.name;
                    }
                  }
                  return undefined;
                })();

                const findSystemPrompt = (name: string | undefined) =>
                  name ? subagentPrompts?.find((p) => p.agentName === name) : undefined;

                const matchedSystemPrompt = findSystemPrompt(agentName);

                const externalAgentResult =
                  result &&
                  !result.isError &&
                  toolName === 'external__callAgent' &&
                  result.result &&
                  typeof result.result === 'object'
                    ? (result.result as {
                        messages?: ExternalAgentMessageInfo[];
                        agentName?: string;
                        agentType?: 'a2a' | 'dify';
                      })
                    : null;

                const fileSubmission =
                  result && !result.isError && !result.isCancelled
                    ? parseFileSubmissionResult(toolName, result.result)
                    : null;

                // showWidget のウィジェットプレビュー
                // deferred__execute 経由の場合は _originalToolName で判定
                const effectiveToolName =
                  toolName === 'deferred__execute' &&
                  result?.result &&
                  typeof result.result === 'object' &&
                  '_originalToolName' in (result.result as Record<string, unknown>)
                    ? ((result.result as Record<string, unknown>)._originalToolName as string)
                    : toolName;

                const widgetResult =
                  effectiveToolName === 'showWidget' &&
                  result &&
                  !result.isError &&
                  result.result &&
                  typeof result.result === 'object'
                    ? (result.result as {
                        title?: string;
                        widgetCode?: string;
                        mode?: 'svg' | 'html';
                      })
                    : null;

                return (
                  <div key={key}>
                    {/* completionCheck は表示専用の合成チャンクのため、生のツールカードは出さず専用カードのみ表示 */}
                    {effectiveToolName !== 'builtin__completionCheck' && (
                      <ToolCallCard
                        toolName={toolName}
                        category={call?.category}
                        status={status}
                        intent={call?.intent}
                        args={call?.args}
                        result={result?.result}
                        isError={result?.isError}
                        isCancelled={result?.isCancelled}
                        approvalOutcome={call?.approvalOutcome}
                        autoApprovedBy={call?.autoApprovedBy}
                        durationMs={result?.durationMs}
                        // 呼び出しごとのスタンプを優先し、持たない古い履歴だけ
                        // タスク単位の記録へ落とす。
                        //
                        // **落とす先はサンドボックスのツールに限る。** タスク単位の
                        // 記録はあらゆるツールのカードへ渡ってくるため、絞らないと
                        // ホスト側で動いた MCP ツールまで「クラウドで実行された」と
                        // 名乗る。監査で読む値が業務データの送り先を誤って主張する
                        sandboxTarget={
                          call?.sandboxTarget ??
                          (call?.category === 'sandbox' ? sandboxTarget : undefined)
                        }
                      />
                    )}
                    {/* 完了チェッカーの作業は、利用者のアプリ設定にかかわらず表示する（監査のため）。
                        作業のあとに判定が出る順にするため、判定結果より先に置く */}
                    {effectiveToolName === 'builtin__completionCheck' &&
                      subAgentMessages &&
                      subAgentMessages.length > 0 && (
                        <SubAgentMessagesCard
                          messages={subAgentMessages}
                          statusLabel={COMPLETION_CHECKER_AGENT_NAME}
                        />
                      )}
                    {effectiveToolName === 'builtin__completionCheck' &&
                      (result?.result ? (
                        <CompletionCheckCard result={result.result} />
                      ) : (
                        // 判定結果がない＝判定中か、判定の途中で中断された。チェッカーの作業は下に並ぶ
                        <CompletionCheckPendingView
                          state={status === 'running' ? 'running' : 'cancelled'}
                          className="mb-2"
                        />
                      ))}
                    {effectiveToolName === 'builtin__advisor' && !!result?.result && (
                      <AdvisorResultCard
                        result={result.result}
                        question={getAdvisorQuestion(call?.args)}
                      />
                    )}
                    {widgetResult?.widgetCode && (
                      <WidgetPreview
                        title={widgetResult.title ?? 'widget'}
                        widgetCode={widgetResult.widgetCode}
                        mode={widgetResult.mode ?? 'html'}
                      />
                    )}
                    {fileSubmission && (
                      <FileSubmissionCard
                        files={fileSubmission.files}
                        message={fileSubmission.message}
                      />
                    )}
                    {effectiveToolName !== 'builtin__completionCheck' &&
                      subAgentMessages &&
                      subAgentMessages.length > 0 &&
                      (() => {
                        // 並列委譲は 1 つのツール呼び出しに複数サブタスクのメッセージが
                        // 混在するため、subTaskId ごとに分けてサブタスク単位で表示する
                        const groups =
                          effectiveToolName === 'subagent__delegateParallelTasks'
                            ? groupSubAgentMessagesBySubTaskId(subAgentMessages)
                            : undefined;

                        // subTaskId を持つメッセージが 1 件も無い履歴は従来どおり 1 枚で表示
                        if (groups && Array.from(groups.keys()).some((id) => id !== undefined)) {
                          return Array.from(groups.entries()).map(([subTaskId, groupMessages]) => {
                            const groupAgentName =
                              (subTaskId && call?.streamingSubTaskAgentNames?.[subTaskId]) ||
                              agentName;
                            return (
                              <SubAgentMessagesCard
                                key={subTaskId ?? 'default'}
                                messages={groupMessages}
                                agentName={groupAgentName}
                                systemPrompt={findSystemPrompt(groupAgentName)}
                              />
                            );
                          });
                        }

                        return (
                          <SubAgentMessagesCard
                            messages={subAgentMessages}
                            agentName={agentName}
                            systemPrompt={matchedSystemPrompt}
                          />
                        );
                      })()}
                    {externalAgentResult?.messages && externalAgentResult.messages.length > 0 && (
                      <ExternalAgentMessagesCard
                        messages={externalAgentResult.messages}
                        agentName={externalAgentResult.agentName}
                        agentType={externalAgentResult.agentType}
                      />
                    )}
                  </div>
                );
              }

              return null;
            });
          })()}
        </>
      ) : (
        <div className="prose prose-invert prose-sm max-w-none">
          <MarkdownContent content={message.content || ''} />
        </div>
      )}
    </div>
  );
}

/**
 * グループ化されたアシスタントメッセージカードコンポーネント
 */

/**
 * 応答を生成したモデルの正式名とエフォートを表示する（Issue #1355）。
 * どのバージョンのモデルのどのエフォートでの出力かを後から確認できるよう、記録された値を名前にし、
 * 記録された ID はツールチップで示す。エイリアスのまま記録された過去の応答は読み替えない。
 */
function ModelInfoLabel({ modelInfo }: { modelInfo: NonNullable<AdminMessageInfo['modelInfo']> }) {
  const name = getModelDisplayName(modelInfo.modelId, modelInfo.provider);
  const effort = getReasoningLevelDisplayLabel(modelInfo.modelId, modelInfo.reasoningLevel);
  return (
    <span
      data-testid="message-model-info"
      title={`${modelInfo.provider}/${modelInfo.modelId}`}
      className="ml-2 text-xs text-muted-foreground"
    >
      ({name}
      {effort ? ` / エフォート: ${effort}` : ''})
    </span>
  );
}

export function GroupedAssistantCard({
  messages,
  subagentPrompts,
  sandboxTarget,
}: {
  messages: AdminMessageInfo[];
  subagentPrompts?: ExtractedPrompt[];
  /** このタスクがどの実行先で実行されたか（サンドボックスのツールに表示する） */
  sandboxTarget?: SandboxTarget | undefined;
}) {
  // 最後のメッセージから統計情報を取得
  const lastMessage = messages[messages.length - 1];
  const totalUsage = lastMessage?.usage;
  const duration = lastMessage?.duration;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden border-l-4 border-l-green-500">
      {/* ヘッダー（1つだけ表示） */}
      <div className="px-4 md:px-6 py-3 bg-muted/50 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="w-8 h-8 shrink-0 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div className="min-w-0 break-all">
            <span className="text-foreground font-medium">エージェント</span>
            {lastMessage?.modelInfo && <ModelInfoLabel modelInfo={lastMessage.modelInfo} />}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {totalUsage && (
            <>
              <span>{formatNumber(totalUsage.totalTokens)} トークン</span>
              {totalUsage.cost !== undefined && (
                <span className="text-yellow-400">{formatCost(totalUsage.cost)}</span>
              )}
            </>
          )}
          {duration && duration > 0 && (
            <span className="inline-flex items-center gap-1 text-blue-400">
              <Clock className="w-3 h-3" />
              {formatDuration(duration)}
            </span>
          )}
        </div>
      </div>

      {/* 各メッセージの本文 */}
      {messages.map((message, index) => (
        <div
          key={message.id}
          className={`px-4 md:px-6 py-4 ${index > 0 ? 'border-t border-border' : ''}`}
        >
          <MessageContent
            message={message}
            subagentPrompts={subagentPrompts}
            sandboxTarget={sandboxTarget}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * メッセージカードコンポーネント
 */
export function MessageCard({
  message,
  subagentPrompts,
  sandboxTarget,
}: {
  message: AdminMessageInfo;
  /** サブエージェントのシステムプロンプト一覧（agentName でマッチング） */
  subagentPrompts?: ExtractedPrompt[];
  /** このタスクがどの実行先で実行されたか（サンドボックスのツールに表示する） */
  sandboxTarget?: SandboxTarget | undefined;
}) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  return (
    <div
      className={`bg-card rounded-xl border border-border overflow-hidden ${
        isUser ? 'border-l-4 border-l-blue-500' : ''
      } ${isAssistant ? 'border-l-4 border-l-green-500' : ''} ${
        isSystem ? 'border-l-4 border-l-orange-500' : ''
      }`}
    >
      {/* ヘッダー */}
      <div className="px-4 md:px-6 py-3 bg-muted/50 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          {isUser ? (
            <div className="w-8 h-8 shrink-0 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
          ) : isSystem ? (
            <div className="w-8 h-8 shrink-0 bg-orange-500/20 text-orange-400 rounded-full flex items-center justify-center">
              <Cog className="w-4 h-4" />
            </div>
          ) : (
            <div className="w-8 h-8 shrink-0 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
          )}
          <div className="min-w-0 break-all">
            <span className="text-foreground font-medium">
              {isUser
                ? 'ユーザー'
                : isAssistant
                  ? 'エージェント'
                  : message.role === 'system'
                    ? 'システムプロンプト'
                    : message.role}
            </span>
            {message.modelInfo && <ModelInfoLabel modelInfo={message.modelInfo} />}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {message.usage && (
            <>
              <span>{formatNumber(message.usage.totalTokens)} トークン</span>
              {message.usage.cost !== undefined && (
                <span className="text-yellow-400">{formatCost(message.usage.cost)}</span>
              )}
            </>
          )}
          {isAssistant && message.duration && message.duration > 0 && (
            <span className="inline-flex items-center gap-1 text-blue-400">
              <Clock className="w-3 h-3" />
              {formatDuration(message.duration)}
            </span>
          )}
          {/* 表示はタスク履歴と同じ省略形式に揃え、正確な日時はホバーで確認できるようにする */}
          <span title={formatFullDateTime(message.createdAt)}>
            {formatDateTime(message.createdAt)}
          </span>
        </div>
      </div>

      {/* 本文 */}
      <div className="px-4 md:px-6 py-4">
        <MessageContent
          message={message}
          subagentPrompts={subagentPrompts}
          sandboxTarget={sandboxTarget}
        />
      </div>
    </div>
  );
}
